import React, { useMemo, useState } from 'react';
import {
  ButtonDropdown,
  DangerText,
  NumberField,
  SelectField,
  TDropdownElementProps,
  Currency,
  Tooltip,
} from 'ui-toolkit';
import { ePayerType, payerTypeOptions, prettyPayerType } from 'types/order/escrow/ePayerType';
import { depositCategoryOptions, eDepositCategory, prettyDepositCategory } from 'types/order/escrow/eDepositCategory';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { StyledTableNumberColumn, StyledTableRow, StyledTableRowColumn } from '../table/StyledTable';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MatchingCheckModalSection } from './MatchingModal/MatchingCheckModalSection';
import { MatchingWireModalSection } from './MatchingModal/MatchingWireModalSection';
import { DepositTooltipContent } from './DepositTooltipContent';
import { useField } from 'formik';
import { useHasPermissions } from '../../../accessControl/hooks/useHasPermissions';

interface IIncomingDepositRowProps {
  fieldName: string;
  index: number;
  onCancelEdit: () => void;
  onDelete: () => void;
  newDeposit: boolean;
  depositData: {
    amountCents: number;
    payerType: ePayerType;
    category: eDepositCategory;
    id: string;
    depositType: string;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy?: string;
  };
  remove: () => void;
  children?: React.ReactNode;
}
export const IncomingDepositRow: React.FC<IIncomingDepositRowProps> = ({
  fieldName,
  index,
  onCancelEdit,
  onDelete,
  newDeposit,
  depositData,
  remove,
}) => {
  const hasIncomingWiresEditPermissions = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresEdit]);
  const hasIncomingChecksEditPermissions = useHasPermissions([ePermissions.EscrowExpectedIncomingChecksEdit]);

  const hasEditPermission = hasIncomingWiresEditPermissions || hasIncomingChecksEditPermissions;
  const [{ value: isEditing }, , { setValue: setIsEditing }] = useField<boolean | undefined>(
    `${fieldName}.${index}.isEditing`
  );
  const kabobItems: TDropdownElementProps[] = useMemo(() => {
    const items: TDropdownElementProps[] = [];
    if (isEditing) {
      items.push({
        children: 'Cancel Edit',
        onClick: () => {
          onCancelEdit();
        },
        'data-test-id': `cancel-edit-wire-${index}`,
      });
    }
    if (!isEditing) {
      items.push(
        {
          children: 'Edit Item',
          onClick: () => setIsEditing(true),
          'data-test-id': `edit-wire-${index}`,
        },
        {
          children: 'Match',
          onClick: () => setMatchModal(true),
        }
      );
    }

    items.push({
      children: <DangerText>Delete</DangerText>,
      onClick: newDeposit ? remove : onDelete,
      'data-test-id': `delete-wire-${index}`,
    });
    return items;
  }, [onDelete, setIsEditing, isEditing, index, newDeposit, remove, onCancelEdit]);

  const [matchModal, setMatchModal] = useState(false);

  return (
    <>
      <Tooltip
        trigger={'hover'}
        placement={'top'}
        tooltipContent={
          <DepositTooltipContent
            createdAt={depositData.createdAt}
            createdBy={depositData.createdBy}
            updatedAt={depositData.updatedAt}
            updatedBy={depositData.updatedBy}
          />
        }
      >
        <StyledTableRow>
          <StyledTableNumberColumn>{(index + 1).toString().padStart(2, '0')}</StyledTableNumberColumn>
          {isEditing && (
            <>
              <NumberField
                name={`${fieldName}.${index}.amountCents`}
                className={`p-4`}
                mode="currency"
                placeholder="123,456.78"
                valueIsCents
                readOnly={!hasEditPermission}
              />
              <SelectField
                name={`${fieldName}.${index}.payerType`}
                options={payerTypeOptions}
                className={`p-4`}
                isDisabled={!hasEditPermission}
              />
              <SelectField
                name={`${fieldName}.${index}.category`}
                options={depositCategoryOptions}
                className={`p-4`}
                isDisabled={!hasEditPermission}
              />
            </>
          )}
          {!isEditing && (
            <>
              <StyledTableRowColumn>
                <Currency value={depositData.amountCents} valueIsCents />
              </StyledTableRowColumn>
              <StyledTableRowColumn>{prettyPayerType[depositData.payerType]}</StyledTableRowColumn>
              <StyledTableRowColumn>{prettyDepositCategory[depositData.category]}</StyledTableRowColumn>
            </>
          )}
          <div className={`self-center justify-self-center`}>
            {hasEditPermission && (
              <ButtonDropdown intent={'ghost'} isInline items={kabobItems} data-test-id={`wire-kabob`}>
                <FontAwesomeIcon className={'px-4'} icon={faEllipsisV} />
              </ButtonDropdown>
            )}
          </div>
        </StyledTableRow>
      </Tooltip>
      {matchModal && depositData.depositType === 'Check' && (
        <MatchingCheckModalSection onClose={() => setMatchModal(false)} expectedCheck={depositData} />
      )}
      {matchModal && depositData.depositType === 'Wire' && (
        <MatchingWireModalSection onClose={() => setMatchModal(false)} expectedWire={depositData} />
      )}
    </>
  );
};
